import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, File, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = BACKEND_URL ? `${BACKEND_URL}/api/employees/upload-excel` : '/api/employees/upload-excel';

const FileUpload = ({ onUploadSuccess, onClose, authHeader }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Проверяем тип файла
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        toast({
          title: "Помилка",
          description: "Будь ласка, оберіть файл Excel (.xlsx або .xls)",
          variant: "destructive",
        });
        return;
      }

      // Проверяем размер файла (максимум 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast({
          title: "Помилка",
          description: "Розмір файлу не повинен перевищувати 10MB",
          variant: "destructive",
        });
        return;
      }

      setFile(selectedFile);
      setUploadStatus('idle');
      setErrorMessage('');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Подготавливаем заголовки для запроса
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      };
      
      // Добавляем заголовок авторизации, если он есть
      if (authHeader) {
        config.headers['Authorization'] = authHeader;
      }
      
      const response = await axios.post(API_URL, formData, config);

      setUploadStatus('success');
      toast({
        title: "Успіх!",
        description: response.data.message,
        variant: "default",
      });

      // Вызываем callback для обновления данных, включая название файла
      if (onUploadSuccess) {
        onUploadSuccess({
          ...response.data,
          filename: file.name
        });
      }

      // Автоматически закрываем через 2 секунды
      setTimeout(() => {
        onClose && onClose();
      }, 2000);

    } catch (error) {
      setUploadStatus('error');
      const errorMsg = error.response?.data?.detail || error.message || 'Помилка завантаження файлу';
      setErrorMessage(errorMsg);
      
      toast({
        title: "Помилка",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      // Создаем событие для handleFileSelect
      const fakeEvent = {
        target: { files: [droppedFile] }
      };
      handleFileSelect(fakeEvent);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>Завантажити Excel файл</span>
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              disabled={uploading}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Область загрузки */}
          {!file && (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-1">
                Перетягніть файл сюди або натисніть для вибору
              </p>
              <p className="text-xs text-gray-500">
                Підтримуються формати: .xlsx, .xls (до 10MB)
              </p>
            </div>
          )}

          {/* Скрытый input для выбора файла */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Информация о выбранном файле */}
          {file && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <File className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                {uploadStatus === 'idle' && (
                  <Button variant="ghost" size="sm" onClick={resetUpload}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Прогресс загрузки */}
              {uploadStatus === 'uploading' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Завантаження...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="w-full" />
                </div>
              )}

              {/* Статус успеха */}
              {uploadStatus === 'success' && (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Файл успішно завантажено!</span>
                </div>
              )}

              {/* Статус ошибки */}
              {uploadStatus === 'error' && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Помилка завантаження</span>
                  </div>
                  {errorMessage && (
                    <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                      {errorMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={onClose}
              disabled={uploading}
            >
              Скасувати
            </Button>
            <Button 
              className="flex-1"
              onClick={handleUpload}
              disabled={!file || uploading || uploadStatus === 'success'}
            >
              {uploading ? 'Завантаження...' : 'Завантажити'}
            </Button>
          </div>

          {/* Информационное сообщение */}
          <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded">
            <p className="font-medium mb-1">Очікуваний формат файлу:</p>
            <ul className="space-y-1">
              <li>• Excel файл з даними співробітників</li>
              <li>• Колонки: ПІП, зарплата, показники роботи</li>
              <li>• Завантаження замінить існуючі дані</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileUpload;